/**
 * 4D Timeliner Component — Playback controls, timeline slider, mini S-curve
 */
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight, Calendar, Target, Filter } from 'lucide-react';

export function FourDTimeliner({
  snapshots, currentIndex, onIndexChange, isPlaying, onTogglePlay, playSpeed, onSpeedChange,
  vizMode, onlyLateCp, onOnlyLateCpChange, onTodayZoom,
}) {
  const total = snapshots?.length || 0;
  const current = snapshots?.[currentIndex];

  const todayIndex = useMemo(() => {
    if (!snapshots || total === 0) return 0;
    const now = new Date();
    const idx = snapshots.findIndex(s => new Date(s.snapshotDate) >= now);
    return idx >= 0 ? idx : total - 1;
  }, [snapshots, total]);

  const todayPct = total > 1 ? (todayIndex / (total - 1)) * 100 : 0;

  const progressData = useMemo(() =>
    (snapshots || []).map(s => ({
      label: s.periodLabel,
      planned: s.metrics?.plannedProgressPct || 0,
      actual: s.metrics?.actualProgressPct || 0,
    })),
  [snapshots]);

  if (!snapshots || total === 0) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500 text-sm" data-testid="timeliner-empty">
        No timeliner data. Link a schedule and generate snapshots.
      </div>
    );
  }

  const delta = (current?.metrics?.actualProgressPct || 0) - (current?.metrics?.plannedProgressPct || 0);
  const isCpMode = vizMode === 'CRITICAL_PATH';

  const handleToday = () => {
    onIndexChange(todayIndex);
    // Give the snapshot fetch a beat to complete before the viewer zooms.
    if (typeof onTodayZoom === 'function') setTimeout(() => onTodayZoom(todayIndex), 250);
  };

  return (
    <div className="h-full flex flex-col px-3 py-2 gap-1" data-testid="timeliner-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(0)} data-testid="timeliner-start"><SkipBack className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(Math.max(0, currentIndex - 1))} data-testid="timeliner-back"><ChevronLeft className="w-3.5 h-3.5" /></Button>
          <Button variant="default" size="sm" className="h-7 px-3 gap-1" onClick={onTogglePlay} data-testid="timeliner-play">
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(Math.min(total - 1, currentIndex + 1))} data-testid="timeliner-forward"><ChevronRight className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onIndexChange(total - 1)} data-testid="timeliner-end"><SkipForward className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">Speed:</span>
          <Select value={String(playSpeed)} onValueChange={v => onSpeedChange(Number(v))}>
            <SelectTrigger className="h-6 w-16 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0.25, 0.5, 1, 2, 4].map(s => <SelectItem key={s} value={String(s)}>{s}x</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Badge variant="outline" className="text-xs h-6 px-2"><Calendar className="w-3 h-3 mr-1" />{current?.periodLabel || '\u2014'}</Badge>
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={handleToday} data-testid="timeliner-today">
          <Target className="w-3 h-3 mr-1" />Today
        </Button>
        {isCpMode && (
          <Button
            variant={onlyLateCp ? 'destructive' : 'ghost'}
            size="sm"
            className="h-6 text-xs gap-1"
            onClick={() => onOnlyLateCpChange && onOnlyLateCpChange(!onlyLateCp)}
            data-testid="timeliner-only-late-cp"
            title="Only show critical-path activities that are in-progress or overdue — strips upcoming CP so PMs can focus on slippage."
          >
            <Filter className="w-3 h-3" />
            Only late CP
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>Plan: <strong className="text-blue-400">{(current?.metrics?.plannedProgressPct || 0).toFixed(1)}%</strong></span>
          <span>Actual: <strong className="text-emerald-400">{(current?.metrics?.actualProgressPct || 0).toFixed(1)}%</strong></span>
          <span className={delta >= 0 ? 'text-emerald-400' : 'text-red-400'}>{delta >= 0 ? '+' : ''}{delta.toFixed(1)}%</span>
        </div>
      </div>
      <div className="relative">
        <input type="range" min={0} max={total - 1} value={currentIndex} onChange={e => onIndexChange(Number(e.target.value))} className="w-full h-1.5 accent-blue-500 cursor-pointer" data-testid="timeliner-slider" />
        <div className="absolute top-0 h-1.5 pointer-events-none" style={{ left: `${todayPct}%` }}><div className="w-0.5 h-3 bg-yellow-400 -mt-0.5" /></div>
        <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
          <span>{snapshots[0]?.periodLabel}</span>
          <span>{snapshots[total - 1]?.periodLabel}</span>
        </div>
      </div>
      <div className="h-16 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={progressData} margin={{ top: 2, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="planGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="planned" stroke="#3B82F6" fill="url(#planGrad)" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
            <Area type="monotone" dataKey="actual" stroke="#10B981" fill="url(#actualGrad)" strokeWidth={1.5} dot={false} />
            {currentIndex < total && <ReferenceLine x={currentIndex} stroke="#F59E0B" strokeWidth={1} strokeDasharray="3 3" />}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
        <span>Visible: <strong>{(current?.metrics?.objectsVisible || 0).toLocaleString()}</strong></span>
        <span>In Progress: <strong>{current?.metrics?.activitiesInProgress || 0}</strong></span>
        <span className="text-red-400">Critical: <strong>{current?.metrics?.criticalPathActivities || 0}</strong></span>
        <span className="text-emerald-400">Completed: <strong>{current?.metrics?.activitiesCompleted || 0}</strong></span>
        <span>Step: {currentIndex + 1} / {total}</span>
      </div>
    </div>
  );
}
